import React, {useState} from 'react';
import {FaInfoCircle} from 'react-icons/fa';
import {HOST} from "../config";
import {useParams, useSearchParams, Link} from "react-router-dom";
import ProgressBar from "../ProgressBar/ProgressBar";
import Flashcard from '../Flashcard/Flashcard';
import MultipleChoice from '../MultipleChoice/MultipleChoice';
import TaskCard from '../TaskCard/TaskCard';
import {useListenMode} from '../ListenMode/useListenMode';
import './StudyMode.css';

// AirPods gesture -> action, per Listen Mode phase (mirrors useListenMode.js).
const LISTEN_MODE_HELP = [
    {phase: 'Question', next: 'Reveal answer', prev: 'Repeat question', playPause: 'Skip card'},
    {phase: 'Answer', next: 'Quick correct', prev: 'Repeat answer', playPause: 'Go to grading'},
    {phase: 'Grading', next: 'Correct', prev: 'Incorrect', playPause: 'Unsure'},
];

const DEFAULT_VOICE = 'en_GB-cori-high';

// Fisher-Yates, pure (doesn't mutate the input).
const shuffle = (arr) => {
    const result = [...arr];
    for (let i = result.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
};

// Progressive difficulty: a flashcard at correct2/correct3 with enough authored
// distractors gets tested as multiple choice instead of a plain flip — true
// recognition among wrong answers is a tougher bar than self-graded recall, and
// correct3 uses deliberately deceptive (harder) ones than correct2 does.
const mcTierFor = (card) => {
    if (card?.type !== 'flashcard') return null;
    if (card.progress === 'correct3' && (card.hardDistractors?.length || 0) >= 2) {
        return {distractors: card.hardDistractors};
    }
    if (card.progress === 'correct2' && (card.distractors?.length || 0) >= 2) {
        return {distractors: card.distractors};
    }
    return null;
};

const EMPTY_PROGRESS = {incorrect: 0, unsure: 0, correct1: 0, correct2: 0, correct3: 0, untouched: 0, accepted: 0};

const StudyMode = ({combined = false}) => {
    const {id: setId} = useParams();
    const [searchParams] = useSearchParams();
    const auto = combined || searchParams.get('auto') === '1';
    // Filter state (for starting a session)
    const [progress, setProgress] = useState({});
    const [filters, setFilters] = useState(null);
    // Cards for the study session
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sessionStarted, setSessionStarted] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);

    // Combined-mode-only: filter the due queue by book and/or topic
    const [booksSummary, setBooksSummary] = useState(null);
    const [selectedBooks, setSelectedBooks] = useState([]);
    const [selectedTopics, setSelectedTopics] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState('due');
    const [showFilters, setShowFilters] = useState(false);

    // Combined-mode-only: Listen Mode (TTS + AirPods control)
    const [listenModeEnabled, setListenModeEnabled] = useState(false);
    const [showListenHelp, setShowListenHelp] = useState(false);
    const [voices, setVoices] = useState([]);
    const [selectedVoice, setSelectedVoice] = useState(
        () => localStorage.getItem('flashcards.ttsVoice') || DEFAULT_VOICE
    );

    const allBookIds = booksSummary?.books.map(b => b.setId) || [];
    const allTopics = booksSummary?.themes || [];
    const bookFilterActive = selectedBooks.length > 0 && selectedBooks.length < allBookIds.length;
    const topicFilterActive = selectedTopics.length > 0 && selectedTopics.length < allTopics.length;

    const dueUrl = React.useMemo(() => {
        if (!combined) return `${HOST}/api/sets/${setId}/due`;
        const params = new URLSearchParams();
        if (bookFilterActive) params.append('book', selectedBooks.join(','));
        if (topicFilterActive) params.append('topic', selectedTopics.join(','));
        if (selectedOrder !== 'due') params.append('order', selectedOrder);
        const qs = params.toString();
        return `${HOST}/api/books/due${qs ? '?' + qs : ''}`;
    }, [combined, setId, bookFilterActive, topicFilterActive, selectedBooks, selectedTopics, selectedOrder]);

    const toggleBookFilter = (bookId) => {
        setSelectedBooks((prev) => {
            const base = prev.length > 0 ? prev : allBookIds;
            return base.includes(bookId) ? base.filter((id) => id !== bookId) : [...base, bookId];
        });
    };

    const toggleTopicFilter = (topicName) => {
        setSelectedTopics((prev) => {
            const base = prev.length > 0 ? prev : allTopics;
            return base.includes(topicName) ? base.filter((t) => t !== topicName) : [...base, topicName];
        });
    };

    React.useEffect(() => {
        if (!combined) return;
        fetch(`${HOST}/api/books/summary`)
            .then((response) => {
                if (!response.ok) throw new Error('Error fetching book summary.');
                return response.json();
            })
            .then((data) => setBooksSummary(data))
            .catch((err) => console.error(err));
    }, [combined]);

    React.useEffect(() => {
        if (!combined) return;
        fetch(`${HOST}/api/voices`)
            .then((response) => {
                if (!response.ok) throw new Error('Error fetching voices.');
                return response.json();
            })
            .then((data) => setVoices(data.voices || []))
            .catch((err) => console.error(err));
    }, [combined]);

    React.useEffect(() => {
        localStorage.setItem('flashcards.ttsVoice', selectedVoice);
    }, [selectedVoice]);

    React.useEffect(() => {
        if (auto) return;
        fetch(`${HOST}/api/sets/${setId}/filter`, {
            method: 'GET',
            headers: {'Content-Type': 'application/json'},
        })
            .then((response) => {
                if (!response.ok) throw new Error('Error updating card status.');
                return response.json();
            })
            .then((cachedFilters) => {
                setFilters(cachedFilters);
            })
            .catch((err) => {
                console.error(err);
            });
    }, [setId, auto])

    // Book sets skip filter setup entirely and jump straight into the cards due for review
    React.useEffect(() => {
        if (!auto) return;
        fetch(dueUrl)
            .then((response) => {
                if (!response.ok) throw new Error('Error fetching due cards.');
                return response.json();
            })
            .then((data) => {
                setCards(data.cards);
                setCurrentIndex(0);
                setSessionStarted(true);
                setProgress(data.cards.reduce(
                    (acc, card) => ({...acc, [card.progress]: (acc[card.progress] || 0) + 1}),
                    {...EMPTY_PROGRESS}
                ));
            })
            .catch((err) => {
                console.error(err);
            });
    }, [dueUrl, auto])

    // Handle filter input changes
    const handleInputChange = (e) => {
        const {name, value, type, checked} = e.target;
        setFilters((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const changeStatus = (status) => {
        if (filters.status.includes(status)) {
            setFilters({
                ...filters,
                status: [...filters.status.filter(stat => stat !== status)],
            })
        } else {
            setFilters({
                ...filters,
                status: [...filters.status, status],
            })
        }
    };

    // Start study session: build query, fetch cards, and start session
    const startStudySession = () => {
        setLoading(true);
        const params = new URLSearchParams();
        if (filters.status) params.append('status', filters.status);
        if (filters.bookmarksOnly) params.append('bookmarksOnly', 'true');
        if (filters.order) params.append('order', filters.order);
        if (filters.limit) params.append('limit', filters.limit);
        if (filters.showFirst) params.append('showFirst', filters.showFirst);
        params.append('excludeHidden', 'true');

        fetch(`${HOST}/api/sets/${setId}/cards?${params.toString()}`)
            .then((response) => {
                if (!response.ok) throw new Error('Error fetching cards.');
                return response.json();
            })
            .then((data) => {
                setCards(data.cards);
                setSessionStarted(true);
                setCurrentIndex(0);
                setLoading(false);
                setProgress({
                    'incorrect': 0,
                    'unsure': 0,
                    'correct1': 0,
                    'correct2': 0,
                    'correct3': 0,
                    'untouched': data.cards.length,
                })

                fetch(`${HOST}/api/sets/${setId}/filter`, {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({filter: filters}),
                })
                    .then((response) => {
                        if (!response.ok) throw new Error('Error updating card status.');
                        return response.json();
                    })
                    .catch((err) => {
                        console.error(err);
                    });
            })
            .catch((err) => {
                console.error(err);
                setLoading(false);
            });
    };

    // Update the card's status and move to the next card
    const updateCardStatus = (progressNew) => {
        if (cards.length === 0) return;
        const card = cards[currentIndex];

        if (progressNew === 'correct') {
            if (card.progress === 'correct3' || card.progress === 'correct2') {
                progressNew = 'correct3';
            } else if (card.progress === 'correct1') {
                progressNew = 'correct2';
            } else {
                progressNew = 'correct1';
            }
        }

        fetch(`${HOST}/api/cards/${card._id}/progress`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({progress: progressNew}),
        })
            .then((response) => {
                if (!response.ok) throw new Error('Error updating card status.');
                return response.json();
            })
            .then(() => {
                setCurrentIndex((prev) => prev + 1);
                setProgress((prev) => {
                    const next = {...prev};
                    // Move the card out of whatever bucket it actually was in
                    // (not always 'untouched' — a card can already be
                    // correct1/incorrect/etc. from a prior session).
                    next[card.progress] = Math.max(0, (next[card.progress] || 0) - 1);
                    next[progressNew] = (next[progressNew] || 0) + 1;
                    return next;
                });
            })
            .catch((err) => {
                console.error(err);
            });
    };

    // Move on without grading (Listen Mode "skip card" gesture) — the card
    // keeps whatever progress/SRS state it already had.
    const skipCard = () => {
        if (cards.length === 0) return;
        setCurrentIndex((prev) => prev + 1);
    };

    const listenMode = useListenMode({
        enabled: combined && listenModeEnabled,
        card: cards[currentIndex],
        voice: selectedVoice,
        onGrade: updateCardStatus,
        onSkip: skipCard,
    });

    const toggleListenMode = () => {
        const next = !listenModeEnabled;
        setListenModeEnabled(next);
        if (next) listenMode.activate();
    };

    // Permanently exclude a card from future study sessions and drop it from this one
    const hideCard = () => {
        if (cards.length === 0) return;
        const card = cards[currentIndex];

        fetch(`${HOST}/api/cards/${card._id}/hidden`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({hidden: true}),
        })
            .then((response) => {
                if (!response.ok) throw new Error('Error hiding card.');
                return response.json();
            })
            .then(() => {
                setCards((prev) => prev.filter((_, i) => i !== currentIndex));
                setProgress((prev) => ({
                    ...prev,
                    [card.progress]: Math.max(0, (prev[card.progress] || 0) - 1),
                }));
            })
            .catch((err) => {
                console.error(err);
            });
    };

    // Build the synthetic MC card once per card (not on every re-render), so the
    // shuffled option order stays stable while the user is answering.
    const currentCard = cards[currentIndex];
    const mcCard = React.useMemo(() => {
        const tier = mcTierFor(currentCard);
        if (!tier) return null;
        return {
            ...currentCard,
            type: 'singleChoice',
            question: currentCard.front,
            options: shuffle([
                {label: currentCard.back, correct: true},
                ...tier.distractors.map((label) => ({label, correct: false})),
            ]),
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentCard?._id]);

    if (!auto && !filters) {
        return '<div>Loading...</div>';
    }

    if (auto && !sessionStarted) {
        return <div>Loading due cards...</div>;
    }

    // If study session hasn't started, show filter panel
    if (!sessionStarted) {
        return (
            <div className="study-mode">
                <h2>Study Mode Setup</h2>
                <div className="filter-panel">
                    <div className="filter-group checkboxes">
                        <label>
                            <input
                                type="checkbox"
                                name="correct3"
                                checked={filters.status.includes('correct3')}
                                onChange={() => changeStatus('correct3')}
                            />
                            3x Correct
                        </label>
                        <label>
                            <input
                                type="checkbox"
                                name="correct2"
                                checked={filters.status.includes('correct2')}
                                onChange={() => changeStatus('correct2')}
                            />
                            2x Correct
                        </label>
                        <label>
                            <input
                                type="checkbox"
                                name="correct1"
                                checked={filters.status.includes('correct1')}
                                onChange={() => changeStatus('correct1')}
                            />
                            1x Correct
                        </label>
                        <label>
                            <input
                                type="checkbox"
                                name="unsure"
                                checked={filters.status.includes('unsure')}
                                onChange={() => changeStatus('unsure')}
                            />
                            Unsure
                        </label>
                        <label>
                            <input
                                type="checkbox"
                                name="incorrect"
                                checked={filters.status.includes('incorrect')}
                                onChange={() => changeStatus('incorrect')}
                            />
                            Incorrect
                        </label>
                        <label>
                            <input
                                type="checkbox"
                                name="untouched"
                                checked={filters.status.includes('untouched')}
                                onChange={() => changeStatus('untouched')}
                            />
                            Not Learned
                        </label>
                    </div>
                    <div className="filter-group">
                        <label>
                            <input
                                type="checkbox"
                                name="bookmarksOnly"
                                checked={filters.bookmarksOnly}
                                onChange={handleInputChange}
                            />
                            Bookmarks Only
                        </label>
                    </div>
                    <div className="filter-group">
                        <label htmlFor="showFirst">Show First:</label>
                        <select
                            id="showFirst"
                            name="showFirst"
                            value={filters.showFirst}
                            onChange={handleInputChange}
                        >
                            <option value="term">Term</option>
                            <option value="definition">Definition</option>
                        </select>
                    </div>
                    <div className="filter-group">
                        <label htmlFor="order">Order:</label>
                        <select
                            id="order"
                            name="order"
                            value={filters.order}
                            onChange={handleInputChange}
                        >
                            <option value="original">Original</option>
                            <option value="random">Random</option>
                        </select>
                    </div>
                    <div className="filter-group">
                        <label htmlFor="limit">Limit:</label>
                        <input
                            type="number"
                            id="limit"
                            name="limit"
                            value={filters.limit}
                            onChange={handleInputChange}
                            min="1"
                            max="50"
                        />
                    </div>
                    <button onClick={startStudySession} disabled={loading}>
                        {loading ? 'Loading...' : 'Start Study Session'}
                    </button>
                </div>
            </div>
        );
    }

    // When session is complete, show a completion message
    if (currentIndex >= cards.length) {
        return (
            <div className="study-mode end">
                <h2>{cards.length === 0 ? 'Nothing due right now!' : 'Study Session Complete!'}</h2>
                {auto ? (
                    <Link to="/">Back to Sets</Link>
                ) : (
                    <button onClick={() => setSessionStarted(false)}>
                        Back to Filters
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className="learning-container">
            <div className="study-position">
                Card {currentIndex + 1} of {cards.length}
                {combined && booksSummary && (
                    <button className="filter-toggle" onClick={() => setShowFilters(!showFilters)}>
                        Filter{(bookFilterActive || topicFilterActive || selectedOrder !== 'due') ? ' •' : ''}
                    </button>
                )}
                {combined && (
                    <button
                        className={`filter-toggle${listenModeEnabled ? ' active' : ''}`}
                        onClick={toggleListenMode}
                    >
                        {listenModeEnabled ? (listenMode.speaking ? 'Listening…' : `Listening (${listenMode.phase})`) : 'Listen'}
                    </button>
                )}
                {combined && (
                    <button
                        className="listen-help-toggle"
                        onClick={() => setShowListenHelp(!showListenHelp)}
                        title="What do the AirPods button presses do?"
                    >
                        <FaInfoCircle/>
                    </button>
                )}
            </div>
            {combined && showListenHelp && (
                <div className="listen-help">
                    <table>
                        <thead>
                            <tr>
                                <th>Phase</th>
                                <th>⏭ Next</th>
                                <th>⏮ Prev</th>
                                <th>▶/⏸</th>
                            </tr>
                        </thead>
                        <tbody>
                            {LISTEN_MODE_HELP.map((row) => (
                                <tr key={row.phase}>
                                    <td>{row.phase}</td>
                                    <td>{row.next}</td>
                                    <td>{row.prev}</td>
                                    <td>{row.playPause}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {combined && showFilters && booksSummary && (
                <div className="study-filters">
                    <div className="study-filters-group">
                        <span className="study-filters-label">Book</span>
                        {booksSummary.books.map((b) => (
                            <label key={b.setId}>
                                <input
                                    type="checkbox"
                                    checked={selectedBooks.length === 0 || selectedBooks.includes(b.setId)}
                                    onChange={() => toggleBookFilter(b.setId)}
                                />
                                {b.title.replace(/ Flashcards$/, '')}
                            </label>
                        ))}
                    </div>
                    {allTopics.length > 1 && (
                        <div className="study-filters-group">
                            <span className="study-filters-label">Topic</span>
                            {allTopics.map((t) => (
                                <label key={t}>
                                    <input
                                        type="checkbox"
                                        checked={selectedTopics.length === 0 || selectedTopics.includes(t)}
                                        onChange={() => toggleTopicFilter(t)}
                                    />
                                    {t}
                                </label>
                            ))}
                        </div>
                    )}
                    <div className="study-filters-group">
                        <span className="study-filters-label">Order</span>
                        <select value={selectedOrder} onChange={(e) => setSelectedOrder(e.target.value)}>
                            <option value="due">Due date</option>
                            <option value="original">Original</option>
                            <option value="random">Random</option>
                        </select>
                    </div>
                    {voices.length > 0 && (
                        <div className="study-filters-group">
                            <span className="study-filters-label">Voice</span>
                            <select value={selectedVoice} onChange={(e) => setSelectedVoice(e.target.value)}>
                                {voices.map((v) => (
                                    <option key={v.id} value={v.id}>{v.label}</option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>
            )}
            {cards[currentIndex].type === 'flashcard' && !mcCard && (
                <Flashcard
                    card={cards[currentIndex]}
                    interactive={true}
                    onHide={hideCard}
                    {...(listenModeEnabled ? {flipped: listenMode.flipped, onFlip: listenMode.handleFlip} : {})}
                />
            )}
            {mcCard && (
                <MultipleChoice card={mcCard} updateCardStatus={updateCardStatus} interactive={true} onHide={hideCard}/>
            )}
            {['multipleChoice', 'singleChoice'].includes(cards[currentIndex].type) && (
                <MultipleChoice card={cards[currentIndex]} updateCardStatus={updateCardStatus} interactive={true} onHide={hideCard}/>
            )}
            {cards[currentIndex].type === 'task' && (
                <TaskCard card={cards[currentIndex]} onHide={hideCard}/>
            )}
            <div className="study-footer">
                <div className="footer-info">
                    <ProgressBar {...progress} />
                </div>
                {cards[currentIndex].type === 'flashcard' && !mcCard && (
                    <div className="study-controls">
                        <button className="btn-incorrect" onClick={() => updateCardStatus('incorrect')}>
                            Incorrect
                        </button>
                        <button className="btn-unsure" onClick={() => updateCardStatus('unsure')}>
                            Unsure
                        </button>
                        <button className="btn-correct" onClick={() => updateCardStatus('correct')}>
                            Correct
                        </button>
                    </div>
                )}
                {cards[currentIndex].type === 'task' && (
                    <div className="study-controls">
                        <button className="btn-correct" onClick={() => updateCardStatus('accepted')}>
                            Accepted
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StudyMode;
